import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMatchFlow } from './useMatchFlow';
import EventProgressNavBar from './EventProgressNavBar';
import EndMatchBottomSheet from './EndMatchBottomSheet';
import { useToaster } from '../../context/ToasterContext';
import { LuChevronLeft, LuChevronRight, LuCheck, LuCircle } from 'react-icons/lu';
import { enforceSpeedmatchState, bindRoutePulseEnforcer } from '../../utils/speedmatchEnforcer';

export default function CodeVerificationPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const shakingRef = useRef(false);
  const closingRef = useRef(false);

  const {
    socket,
    match: currentMatch,
    setMatch,
    setIsFinding,
    initVerification,
    verifyCode,
    verifyCodeStep,    
    foundPartner,
    endSession,
    errorMsg,
    setErrorMsg,
  } = useMatchFlow();

  const userId    = localStorage.getItem('userId')    || null;
  const sessionId = localStorage.getItem('sessionId') || null;

  const api = process.env.REACT_APP_API_URL;
  const eventId =
    (currentMatch?.event?._id) ||
    localStorage.getItem('mobileEventId') ||
    null;

  const { addToast } = useToaster();

  // ────────────────── NEW: refresh-safe routing control ──────────────────
  const LAST_MATCH_KEY = 'sm:lastFoundMatchId';
  const [rehydrateTried, setRehydrateTried] = useState(false);
  const enforcerCheckedRef = useRef(false);
  const [enforcerDone, setEnforcerDone] = useState(false);
  // Rehydrate the match FIRST (from sessionStorage) on hard refresh
  useEffect(() => {
    if (currentMatch?._id) { setRehydrateTried(true); return; }
    const lastId = sessionStorage.getItem(LAST_MATCH_KEY);
    if (!lastId) { setRehydrateTried(true); return; }

    (async () => {
      try {
        const r = await fetch(`${api}/matches/${lastId}`, {
          headers: {
            Authorization:
              localStorage.getItem('accessToken') ||
              JSON.parse(localStorage.getItem('tribe_auth') || '{}')?.token
                ? `Bearer ${
                    localStorage.getItem('accessToken') ||
                    JSON.parse(localStorage.getItem('tribe_auth') || '{}')?.token
                  }`
                : undefined,
          },
        });
        const data = await r.json().catch(() => ({}));
        if (data?.match) setMatch(data.match);
      } catch {}
      setRehydrateTried(true);
    })();
  }, [api, currentMatch?._id, setMatch]);

  // Keep LAST_MATCH_KEY fresh whenever we have a match
  useEffect(() => {
    if (!currentMatch?._id) return;
    try { sessionStorage.setItem(LAST_MATCH_KEY, String(currentMatch._id)); } catch {}
  }, [currentMatch?._id]);



  useEffect(() => {
    if (!currentMatch?._id || !userId) return;
  
    // Suppress during back-intent window
    try {
      const eid = currentMatch?.event?._id || localStorage.getItem('mobileEventId');
      const sup = Number(sessionStorage.getItem(`speedmatch_suppress_verify_init:${eid}`) || 0);
      if (sup && Date.now() < sup) return;
    } catch {}
  
    // Also skip if enforcer still forcing navigate
    try {
      const eid = currentMatch?.event?._id || localStorage.getItem('mobileEventId');
      const forceUntil = Number(sessionStorage.getItem(`speedmatch_force_navigate:${eid}`) || 0);
      if (forceUntil && Date.now() < forceUntil) return;
    } catch {}
  
    // 🔒 One-shot: prevent double emit under StrictMode / rapid remounts
    if (initSentRef.current) return;
    initSentRef.current = true;
  
    initVerification(currentMatch._id, userId, sessionId);
  
    if (currentMatch.event?._id) {
      localStorage.setItem('mobileEventId', currentMatch.event._id);
    }
  }, [currentMatch?._id, userId, sessionId, initVerification, currentMatch?.event?._id]);
  
  // ────────────────── Incorrect code toast (as-is) ──────────────────
  const [codeDigits, setCodeDigits] = useState(['', '', '']);
  const codeDigitsRefs = useRef([useRef(null), useRef(null), useRef(null)]);

  const [aggCodes, setAggCodes] = useState({ code1: ['', '', ''], code2: ['', '', ''] });
  const aggregatorRefs = useRef(Array.from({ length: 6 }, () => React.createRef()));
  const [aggStage, setAggStage] = useState(0);
  const [aggVerified, setAggVerified] = useState([null, null]); // [code1|null, code2|null]

  const [verificationSuccess, setVerificationSuccess] = useState(false);
  // NEW: drive countdown off a fixed deadline
  const successUntilRef = useRef(0);
  const [nowTick, setNowTick] = useState(0); // repaint ticker
  
  const [showEndSheet, setShowEndSheet] = useState(false);
  const [shouldShake, setShouldShake] = useState(false);

  const [tparts, setTparts] = useState({ h: '--', m: '--', s: '--' });
  const hasHours = tparts.h !== '00' && tparts.h !== '--';
  const initSentRef = useRef(false);

  // put near other effects, after initSentRef is declared
useEffect(() => {
  // allow initVerification to run once per *match*
  initSentRef.current = false;
}, [currentMatch?._id]);


  useEffect(() => {
    if (
      errorMsg?.message &&
      (
        errorMsg.message === 'Incorrect code' ||
        errorMsg.message === 'One or more codes were incorrect' ||
        errorMsg.message.toLowerCase().includes('incorrect')
      )
    ) {
      if (!shakingRef.current) {
        shakingRef.current = true;
        setShouldShake(true);
        addToast('Incorrect Code', 'error');
        setErrorMsg(null);
        setTimeout(() => { shakingRef.current = false; }, 600);
      }
    }
  }, [errorMsg, addToast, setErrorMsg]);
// ────────────────── Enforcer: run only after rehydrate attempt (+ bind pulse) ──────────────────
useEffect(() => {
  if (!rehydrateTried) return;                // ← wait for rehydrate attempt
  if (!eventId) {                             // no eventId: nothing to enforce
    enforcerCheckedRef.current = true;
    setEnforcerDone(true);
    return;
  }

  let alive = true;

  (async () => {
    try {
      await enforceSpeedmatchState({
        api,
        eventId,
        pageKind: 'verify',   // verification page
        navigate,
      });
    } finally {
      if (alive) enforcerCheckedRef.current = true;
      setEnforcerDone(true);
    }
  })();

  // 🔑 Bind to cron pulse so we re-run enforcement whenever route_state arrives
  const cleanup = bindRoutePulseEnforcer({
    api,
    eventId,
    pageKind: 'verify',
    navigate,
  });

  return () => {
    alive = false;
    cleanup();
  };
}, [api, eventId, navigate, rehydrateTried]);

// Keep your focus re-enforce (doesn't affect first-load gating)
useEffect(() => {
  if (!eventId) return;
  const onFocus = () => {
    enforceSpeedmatchState({ api, eventId, pageKind: 'verify', navigate });
  };
  window.addEventListener('focus', onFocus);
  return () => window.removeEventListener('focus', onFocus);
}, [api, eventId, navigate]);

// Re-enforce on push (matchUpdated → enforce without refresh)
useEffect(() => {
  if (!eventId) return;
  const onPush = () =>
    enforceSpeedmatchState({ api, eventId, pageKind: 'verify', navigate });
  window.addEventListener('sm:match-updated', onPush);
  return () => window.removeEventListener('sm:match-updated', onPush);
}, [api, eventId, navigate]);

  // ────────────────── UPDATED: portal fallback — wait for both gates ──────────────────
  useEffect(() => {
    if (!rehydrateTried || !enforcerCheckedRef.current) return; // ← gate redirect
    if (!currentMatch || !currentMatch._id) {
      const params = new URLSearchParams(location.search);
      const wasExpired = params.get('expired');
      const storedEventId = localStorage.getItem('mobileEventId');
      if (storedEventId) params.set('eventId', storedEventId);
      if (wasExpired) params.set('expired', wasExpired);
      navigate(`/matchflow?${params.toString()}`);
    }
  }, [currentMatch, location.search, navigate, rehydrateTried]);

  /* initVerification on mount */
  useEffect(() => {
    const eid = currentMatch?.event?._id || localStorage.getItem('mobileEventId');
  
    if (currentMatch?.codeConfirmed) {
      const until = Date.now() + 3200; // 3.2s buffer (UI shows 3..2..1)
      successUntilRef.current = until - 200; // ~3.0s visible window
  
      // enforcer hold
      if (eid) {
        try { sessionStorage.setItem(`speedmatch_verify_success_hold:${eid}`, String(until)); } catch {}
      }
  
      setVerificationSuccess(true);
      setAggStage(0);
      setAggVerified([null, null]);
    } else {
      successUntilRef.current = 0;
      if (eid) {
        try { sessionStorage.removeItem(`speedmatch_verify_success_hold:${eid}`); } catch {}
      }
      setVerificationSuccess(false);
      setAggStage(0);
      setAggVerified([null, null]);
    }
  }, [currentMatch?.codeConfirmed, currentMatch?.event?._id]);
  

  useEffect(() => {
    if (!verificationSuccess || !successUntilRef.current) return;
    const id = setInterval(() => setNowTick(Date.now()), 150); // lightweight repaint
    return () => clearInterval(id);
  }, [verificationSuccess]);
  

  useEffect(() => {
    if (!verificationSuccess || !successUntilRef.current) return;
  
    if (Date.now() >= successUntilRef.current) {
      const eid = currentMatch?.event?._id || localStorage.getItem('mobileEventId');
      try { sessionStorage.removeItem(`speedmatch_verify_success_hold:${eid}`); } catch {}
  
      if (currentMatch && (currentMatch.status === 'verifying' || currentMatch.status === 'verifying-trio')) {
        foundPartner(currentMatch._id, userId);
      }
      navigate('/inmatchpage');
      return;
    }
    // no-op; repaints are driven by the 150ms interval
  }, [verificationSuccess, nowTick, currentMatch, userId, foundPartner, navigate]);
  

  // Big header timer tick (independent of toasts)
  useEffect(() => {
    const endsAt = currentMatch?.expiresAt;
    if (!endsAt) {
      setTparts({ h: '--', m: '--', s: '--' });
      return;
    }
    const target = new Date(endsAt);
    const tick = () => {
      const diff = target.getTime() - Date.now();
      if (diff <= 0) { setTparts({ h: '00', m: '00', s: '00' }); return; }
      const total = Math.floor(diff / 1000);
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      setTparts({
        h: String(h).padStart(2, '0'),
        m: String(m).padStart(2, '0'),
        s: String(s).padStart(2, '0'),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [currentMatch?.expiresAt]);

  /* completed => /matchflow */
  useEffect(() => {
    if (currentMatch && currentMatch.status === 'completed') {
      const eId = currentMatch.event?._id || localStorage.getItem('mobileEventId');
      navigate(eId ? `/matchflow?eventId=${eId}` : '/matchflow');
    }
  }, [currentMatch, navigate]);

  /* MATCH_EXPIRED */
  useEffect(() => {
    if (!socket) return;
    const handleMatchExpired = ({ matchId }) => {
      if (!currentMatch || currentMatch._id !== matchId) return;
      if (closingRef.current) return;
      closingRef.current = true;
      addToast('Your match expired due to inactivity.', 'error');
      setMatch(null);
      setIsFinding(false);
      navigate('/matchflow');
    };
    socket.on('match_expired', handleMatchExpired);
    return () => socket.off('match_expired', handleMatchExpired);
  }, [socket, currentMatch, setMatch, setIsFinding, navigate, addToast]);

  /* MATCH_ENDED */
  useEffect(() => {
    if (!socket) return;
    const handleMatchEnded = ({ matchId, endedBy }) => {
      if (!currentMatch || currentMatch._id !== matchId) return;
      if (closingRef.current) return;
      closingRef.current = true;

      if (String(endedBy) === String(userId)) {
        addToast('You ended the match early.', 'error');
        navigate('/matchflow?ended=me');
      } else {
        addToast('Your partner ended the match early.', 'error');
        navigate('/matchflow?ended=partner');
      }
      setMatch(null);
      setIsFinding(false);
    };
    socket.on('match_ended', handleMatchEnded);
    return () => socket.off('match_ended', handleMatchEnded);
  }, [socket, currentMatch, userId, navigate, addToast, setMatch, setIsFinding]);

  // ───────────── Fallback UI (unchanged) ─────────────
  if (!rehydrateTried || !enforcerDone) return null;
  if (!currentMatch || !currentMatch._id) {
    return (
      <div style={ST.fallbackContainer}>
        <h2 style={ST.fallbackTitle}>No match info found</h2>
        <p style={ST.fallbackText}>
          We couldn’t locate an active match. Let’s get you back to the portal.
        </p>
        <button style={ST.fallbackBtn} onClick={() => navigate('/matchflow?expired=1')}>
          Back to Events
        </button>
      </div>
    );
  }

  function handleEndMatch() {
    setShowEndSheet(true);
  }

  const step = 3; // always show step 3/5 for verification

  const secsLeft = (() => {
    if (!verificationSuccess || !successUntilRef.current) return null;
    const ms = successUntilRef.current - Date.now();
    return Math.max(0, Math.ceil(ms / 1000));
  })();
  

  // group rendering (unchanged)
  const groupSize = currentMatch.participants?.length || 0;
  let codeUI;
  if (groupSize === 3) codeUI = renderThreePersonUI(); // trio first to avoid transient 2
  else if (groupSize === 2) codeUI = renderTwoPersonUI();
  else codeUI = <p style={{ textAlign: 'center' }}>Unhandled group size: {groupSize}</p>;

  // canSubmit & submit handler for action bar (unchanged)
  const { showEnter, canSubmit, onEnter } = computeActionBarState();

  return (
    <div style={ST.page}>
            <style>
        {`
          @keyframes codeShake {
            0%, 100% { transform: translateX(0); }
            10%, 90%  { transform: translateX(-4px); }
            20%, 80%  { transform: translateX(4px); }
            30%, 50%, 70% { transform: translateX(-8px); }
            40%, 60%  { transform: translateX(8px); }
          }
        `}
      </style>

      {/* Top Nav */}
      <EventProgressNavBar
        title="MatchFlow"
        leftLabel="End"
        onLeftClick={handleEndMatch}
        currentStep={step}
        totalSteps={5}
      />

      {/* Header + timer */}
      <div style={ST.header.container}>
        <h1 style={ST.header.title}>
          Enter your{' '}
          <span style={ST.header.highlight}>
            <span style={ST.header.highlightText}>Code</span>
          </span>
        </h1>

        <div style={ST.header.subheading}>
          Share or input the 3-character code to confirm your match.
        </div>

        <div style={ST.timerWrapper}>
          <div style={ST.timerLabelPill}>Timer Ends In</div>
          <div style={ST.digitsRow} aria-live="polite">
            {hasHours && (
              <>
                <span style={ST.digit}>{tparts.h[0]}</span>
                <span style={ST.digit}>{tparts.h[1]}</span>
                <span style={ST.colon}>:</span>
              </>
            )}
            <span style={ST.digit}>{tparts.m[0]}</span>
            <span style={ST.digit}>{tparts.m[1]}</span>
            <span style={ST.colon}>:</span>
            <span style={ST.digit}>{tparts.s[0]}</span>
            <span style={ST.digit}>{tparts.s[1]}</span>
          </div>
        </div>
      </div>

      {/* Single, generic divider under timer */}
      {(() => {
        const size = currentMatch?.participants?.length || 0;
        const iAmHolder     = String(currentMatch?.codeHolderId)     === String(userId);
        const iAmAggregator = String(currentMatch?.codeAggregatorId) === String(userId);

        let label = 'Enter partner code';
        if (size === 2) {
          label = iAmHolder ? 'Share this code' : (currentMatch?.codeValue ? 'Enter partner code' : 'Waiting for partner');
        } else if (size === 3) {
          label = iAmAggregator ? (aggStage === 0 ? 'Enter first partner code' : 'Enter second partner code')
                                : (currentMatch?.codeMap?.[userId] ? 'Share this code' : 'Awaiting request');
        }

        return (
          <div style={ST.dividerRow} aria-live="polite">
            <div style={ST.dividerLine} />
            <div style={ST.dividerText}>{label}</div>
            <div style={ST.dividerLine} />
          </div>
        );
      })()}

      {/* Code section */}
      <div style={ST.code.container}>
        {codeUI}
      </div>
      {/* Action bar */}
      {!currentMatch?.codeConfirmed && !verificationSuccess && (
        <div style={ST.actionBar}>
          <button
            onClick={async () => {
              try {
                // Tell server: I’m not “found” anymore → back to navigating
                socket?.emit('revertToNavigating', { matchId: currentMatch?._id });
              } catch {}
              // Make sure NavigateSpot can rehydrate even on hard refresh:
              try {
                const LAST_MATCH_KEY = 'sm:lastFoundMatchId';
                if (currentMatch?._id) sessionStorage.setItem(LAST_MATCH_KEY, String(currentMatch._id));
                const eid0 = currentMatch?.event?._id || localStorage.getItem('mobileEventId');
                if (eid0) localStorage.setItem('mobileEventId', String(eid0));
              } catch {}

              // Short client lock so enforcer won’t immediately bounce back
              try {
                const eid1 = currentMatch?.event?._id || localStorage.getItem('mobileEventId');
                if (eid1) {
                  sessionStorage.setItem(
                    `speedmatch_back_block:${eid1}`,
                    String(Date.now() + 1500)
                  );
                }
              } catch {}

              // 0) Seed recovery so NavigateSpot can render instantly on refresh
              try {
                const LAST_MATCH_KEY = 'sm:lastFoundMatchId';
                if (currentMatch?._id) sessionStorage.setItem(LAST_MATCH_KEY, String(currentMatch._id));
                const eid2 = currentMatch?.event?._id || localStorage.getItem('mobileEventId');
                if (eid2) localStorage.setItem('mobileEventId', String(eid2));
              } catch {}

            // 1) Mark a strong "back intent" so enforcer treats verify → navigate immediately
try {
  const eid3 = currentMatch?.event?._id || localStorage.getItem('mobileEventId');
  if (eid3) {
    // hard hint: for the next ~2.5s force navigate
    const until = Date.now() + 2500;
    sessionStorage.setItem(`speedmatch_force_navigate:${eid3}`, String(until));
    // soft hint: don’t pull us back into verify during this window
    sessionStorage.setItem(`speedmatch_back_block:${eid3}`, String(until));
    // prevent verify init from firing during this window on a stale mount
    sessionStorage.setItem(`speedmatch_suppress_verify_init:${eid3}`, String(until));
  }
} catch {}


              // 2) Optimistic client-side move (no server wait)
              navigate('/navigatespotpage', {
                replace: true,
                state: { optimistic: true, matchId: currentMatch?._id },
              });
                        }}
            style={{ ...ST.actionSecondary, flex: 1 }}
          >
            Back
          </button>
          {showEnter && (
            <button
              onClick={onEnter}
              disabled={!canSubmit}
              style={{
                ...ST.actionButton,
                flex: 2,
                ...(canSubmit ? {} : ST.actionButtonDisabled),
              }}
            >
              Enter
            </button>
          )}
        </div>
      )}


      <EndMatchBottomSheet
        visible={showEndSheet}
        fromInSession={false}
        onCancel={() => setShowEndSheet(false)}
        onConfirm={() => {
          if (currentMatch?._id && userId) {
            endSession(currentMatch._id, userId, sessionId);
          }
          setShowEndSheet(false);
        }}
      />
    </div>
  );

  /* =================== 2-PERSON =================== */
  function renderTwoPersonUI() {
    const { codeHolderId, codeValue, codeConfirmed, participants } = currentMatch;
    const iAmHolder = String(codeHolderId) === String(userId);

    if (codeConfirmed && verificationSuccess) return renderSuccessOnly();

    const partner = participants?.find((p) => String(p._id) !== String(userId));
    const partnerName = partner
      ? `${partner.firstName} ${partner.lastName ? partner.lastName.charAt(0).toUpperCase() + '.' : ''}`.trim()
      : 'Your partner';

    if (iAmHolder) {
      if (!codeConfirmed) {
        return (
          <>
            <div style={{ ...ST.boxRow, animation: shouldShake ? 'codeShake 0.6s' : 'none' }}>
              {(codeValue || '---').toUpperCase().split('').map((digit, idx) => (
                <div key={idx} style={ST.readOnlyBox}>
                  {digit || '0'}
                </div>
              ))}
            </div>
            <div style={ST.helper}>
              Share this code with{' '}
              <span style={ST.namePill}>
                <img
                  src={(partner && partner.profilePicUrl) || '/default-avatar.png'}
                  alt=""
                  style={ST.nameAvatar}
                />
                {partnerName}
              </span>.
            </div>

            {/* 2-person: always-show pager under helper */}
            <div style={ST.pagerRow} role="group" aria-label="Partner pagination">
              <button type="button" disabled aria-disabled style={{ ...ST.pagerBtn, ...ST.pagerBtnDisabled }}>
                <LuChevronLeft style={ST.pagerIcon} aria-hidden />
              </button>
              <span style={ST.pagerText}>1/1</span>
              <button type="button" disabled aria-disabled style={{ ...ST.pagerBtn, ...ST.pagerBtnDisabled }}>
                <LuChevronRight style={ST.pagerIcon} aria-hidden />
              </button>
            </div>
          </>
        );
      }
      return renderSuccessBlock();
    }

    // Not holder
    if (!codeValue && !codeConfirmed) {
      return (
        <>
          <div style={ST.helper}>
            Waiting for{' '}
            <span style={ST.namePill}>
              <img
                src={(partner && partner.profilePicUrl) || '/default-avatar.png'}
                alt=""
                style={ST.nameAvatar}
              />
              {partnerName}
            </span>{' '}
            to generate a code…
          </div>

          {/* 2-person: always-show pager under helper */}
          <div style={ST.pagerRow} role="group" aria-label="Partner pagination">
            <button type="button" disabled aria-disabled style={{ ...ST.pagerBtn, ...ST.pagerBtnDisabled }}>
              <LuChevronLeft style={ST.pagerIcon} aria-hidden />
            </button>
            <span style={ST.pagerText}>1/1</span>
            <button type="button" disabled aria-disabled style={{ ...ST.pagerBtn, ...ST.pagerBtnDisabled }}>
              <LuChevronRight style={ST.pagerIcon} aria-hidden />
            </button>
          </div>
        </>
      );
    }

    if (!codeConfirmed) {
      return (
        <>
          <div style={{ ...ST.boxRow, animation: shouldShake ? 'codeShake 0.6s' : 'none' }}>
            {codeDigits.map((digit, idx) => (
              <input
                key={idx}
                type="text"
                inputMode="text"
                autoComplete="off"
                maxLength={1}
                placeholder="0"
                ref={codeDigitsRefs.current[idx]}
                value={(digit || '').toUpperCase()}
                onChange={(e) => handle2pChange(idx, e)}
                onKeyDown={(e) => handle2pKeyDown(idx, e)}
                style={ST.inputBox}
                onAnimationEnd={(e) => {
                  if (e.animationName === 'codeShake') {
                    setCodeDigits(['', '', '']);
                    codeDigitsRefs.current[0]?.current?.focus();
                    setShouldShake(false);
                  }
                }}
              />
            ))}
          </div>

          <div style={ST.helper}>
            Input{' '}
            <span style={ST.namePill}>
              <img
                src={(partner && partner.profilePicUrl) || '/default-avatar.png'}
                alt=""
                style={ST.nameAvatar}
              />
              {partnerName}’s
            </span>{' '}
            code here.
          </div>

          {/* 2-person: always-show pager under helper */}
          <div style={ST.pagerRow} role="group" aria-label="Partner pagination">
            <button type="button" disabled aria-disabled style={{ ...ST.pagerBtn, ...ST.pagerBtnDisabled }}>
              <LuChevronLeft style={ST.pagerIcon} aria-hidden />
            </button>
            <span style={ST.pagerText}>1/1</span>
            <button type="button" disabled aria-disabled style={{ ...ST.pagerBtn, ...ST.pagerBtnDisabled }}>
              <LuChevronRight style={ST.pagerIcon} aria-hidden />
            </button>
          </div>
        </>
      );
    }

    return renderSuccessBlock();
  }

  function handle2pChange(idx, e) {
    const sanitize = (c) => (c || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 1);
    const val = sanitize(e.target.value);
    setCodeDigits((prev) => {
      const next = [...prev];
      next[idx] = val;
      if (val && idx < 2) codeDigitsRefs.current[idx + 1]?.current?.focus();
      return next;
    });
  }

  function handle2pKeyDown(idx, e) {
    if (e.key === 'Backspace') {
      e.preventDefault();
      setCodeDigits((prev) => {
        const next = [...prev];
        if (next[idx]) { next[idx] = ''; return next; }
        if (idx > 0) { codeDigitsRefs.current[idx - 1]?.current?.focus(); next[idx - 1] = ''; }
        return next;
      });
    }
    if (e.key.length === 1) {
      const c = e.key.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 1);
      if (c) {
        e.preventDefault();
        setCodeDigits((prev) => {
          const next = [...prev];
          next[idx] = c;
          if (idx < 2) codeDigitsRefs.current[idx + 1]?.current?.focus();
          return next;
        });
      }
    }
  }

  /* =================== 3-PERSON (STAGED) =================== */
  function renderThreePersonUI() {
    const { codeAggregatorId, codeConfirmed, codeMap, participants } = currentMatch;
    const iAmAggregator = String(codeAggregatorId) === String(userId);

    if (codeConfirmed && verificationSuccess) return renderSuccessOnly();

    /* Aggregator: stage 0 => first partner, stage 1 => second partner */
    if (iAmAggregator) {
      const all = participants || [];
      const partners = all.filter((p) => String(p._id) !== String(userId)).slice(0, 2);

      const pageCount = Math.max(1, partners.length);
      const currentIdx = Math.max(0, Math.min(pageCount - 1, aggStage)); // lock to stage
      const currentPartner = partners[currentIdx];

      const codeKey = currentIdx === 0 ? 'code1' : 'code2';
      const baseRef = currentIdx * 3; // 0..2 for code1, 3..5 for code2

      return (
        <>
          {/* 1) Input row for the active partner (stage-locked) */}
          <div style={{ ...ST.boxRow, animation: shouldShake ? 'codeShake 0.6s' : 'none', marginTop: 12 }}>
            {aggCodes[codeKey].map((digit, i2) => {
              const globalIdx = baseRef + i2;
              return (
                <input
                  key={globalIdx}
                  type="text"
                  inputMode="text"
                  autoComplete="off"
                  maxLength={1}
                  placeholder="0"
                  ref={aggregatorRefs.current[globalIdx]}
                  value={(digit || '').toUpperCase()}
                  onChange={(e) => handleAggChange(e, globalIdx, currentIdx, i2)}
                  onKeyDown={(e) => handleAggKeyDown(e, globalIdx, currentIdx, i2)}
                  style={ST.inputBox}
                  onAnimationEnd={(e) => {
                    if (e.animationName === 'codeShake') {
                      setAggCodes({ code1: ['', '', ''], code2: ['', '', ''] });
                      setAggStage(0);
                      setAggVerified([null, null]);
                      aggregatorRefs.current[0]?.current?.focus();
                      setShouldShake(false);
                    }
                  }}
                />
              );
            })}
          </div>

          {/* 2) Title under input */}
          <div style={ST.trioTitle}>
            {aggStage === 0 ? 'Input first partner’s code' : 'Input second partner’s code'}
          </div>

          {/* 3) Chips (partners only) with status */}
          <div style={ST.trioRow} aria-label="Participants">
            {partners.map((p, idx) => {
              const isActive = aggStage === idx;
              // First pill: green after partial verify ok (aggVerified[0] captured)
              // Second pill: green only when server confirms the final pair (codeConfirmed)
              const isVerified =
                idx === 0
                  ? typeof aggVerified[0] === 'string' && aggVerified[0].length === 3
                  : !!currentMatch?.codeConfirmed;

              return (
                <span
                  key={p?._id || idx}
                  style={{
                    ...ST.trioChip,
                    ...(isActive ? ST.trioChipActive : {}),
                    ...(isVerified ? ST.trioChipDone : {}),
                  }}
                >
                  <img src={(p && p.profilePicUrl) || '/default-avatar.png'} alt="" style={ST.trioAvatar} />
                  <span style={ST.trioName}>
                    {p?.firstName ? `${p.firstName} ${p.lastName ? p.lastName.charAt(0).toUpperCase() + '.' : ''}` : `Partner #${idx + 1}`}
                  </span>
                  {isVerified ? (
                    <LuCheck style={ST.trioIconDone} aria-label="Code verified" />
                  ) : (
                    <LuCircle style={ST.trioIconTodo} aria-hidden />
                  )}
                </span>
              );
            })}
          </div>

          {/* 4) Pager is locked to stage (no skipping ahead) */}
          <div style={ST.pagerRow} role="group" aria-label="Partner pagination">
            <button
              type="button"
              disabled
              aria-disabled
              style={{ ...ST.pagerBtn, ...ST.pagerBtnDisabled }}
            >
              <LuChevronLeft style={ST.pagerIcon} aria-hidden />
            </button>

            <span style={ST.pagerText}>{aggStage + 1}/{pageCount}</span>

            <button
              type="button"
              disabled
              aria-disabled
              style={{ ...ST.pagerBtn, ...ST.pagerBtnDisabled }}
            >
              <LuChevronRight style={ST.pagerIcon} aria-hidden />
            </button>
          </div>
        </>
      );
    }

    /* Non-aggregator (read-only/waiting/share) */
    const aggregator = participants?.find((p) => String(p._id) === String(codeAggregatorId));
    const aggregatorName = aggregator
      ? `${aggregator.firstName} ${aggregator.lastName ? aggregator.lastName.charAt(0).toUpperCase() + '.' : ''}`.trim()
      : 'Aggregator';

    const myCode = codeMap?.[userId];
    if (!myCode && !codeConfirmed) {
      return (
        <>
          <div style={ST.helper}>
            Waiting for{' '}
            <span style={ST.namePill}>
              <img src={(aggregator && aggregator.profilePicUrl) || '/default-avatar.png'} alt="" style={ST.nameAvatar} />
              {aggregatorName}
            </span>{' '}
            to request your code…
          </div>
        </>
      );
    }
    if (!codeConfirmed) {
      return (
        <>
          <div style={ST.boxRow}>
            {(myCode || '---').toUpperCase().split('').map((digit, idx) => (
              <div key={idx} style={ST.readOnlyBox}>
                {digit || '0'}
              </div>
            ))}
          </div>
          <div style={ST.helper}>
            Share this with{' '}
            <span style={ST.namePill}>
              <img src={(aggregator && aggregator.profilePicUrl) || '/default-avatar.png'} alt="" style={ST.nameAvatar} />
              {aggregatorName}
            </span>.
          </div>
        </>
      );
    }
    return renderSuccessBlock();
  }

  function handleAggChange(e, globalIdx, partnerIdx, localIdx) {
    const sanitize = (c) => (c || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 1);
    const val = sanitize(e.target.value);
    setAggCodes((prev) => {
      const key = partnerIdx === 0 ? 'code1' : 'code2';
      const arr = [...prev[key]];
      arr[localIdx] = val;
      return { ...prev, [key]: arr };
    });
    if (val && localIdx < 2) {
      aggregatorRefs.current[globalIdx + 1]?.current?.focus();
    }
  }

  function handleAggKeyDown(e, globalIdx, partnerIdx, localIdx) {
    if (e.key === 'Backspace') {
      e.preventDefault();
      setAggCodes((prev) => {
        const key = partnerIdx === 0 ? 'code1' : 'code2';
        const arr = [...prev[key]];
        // If current cell has a value, clear it
        if (arr[localIdx]) {
          arr[localIdx] = '';
          return { ...prev, [key]: arr };
        }
        // Otherwise move left and clear previous
        if (localIdx > 0) {
          const prevGlobal = globalIdx - 1;
          aggregatorRefs.current[prevGlobal]?.current?.focus();
          arr[localIdx - 1] = '';
          return { ...prev, [key]: arr };
        }
        return prev;
      });
      return;
    }
    if (e.key.length === 1) {
      const c = e.key.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 1);
      if (c) {
        e.preventDefault();
        setAggCodes((prev) => {
          const key = partnerIdx === 0 ? 'code1' : 'code2';
          const arr = [...prev[key]];
          arr[localIdx] = c;
          return { ...prev, [key]: arr };
        });
        if (localIdx < 2) {
          aggregatorRefs.current[globalIdx + 1]?.current?.focus();
        }
      }
    }
  }
  

  /* ============== success helpers ============== */
  function renderSuccessOnly() {
    return (
      <div style={{ textAlign: 'center' }}>
        <p style={ST.successText}>Verification successful!</p>
        {secsLeft !== null && <p style={ST.countdownText}>Continuing in {secsLeft}…</p>}
      </div>
    );
  }
  
  function renderSuccessBlock() {
    return (
      <div style={{ textAlign: 'center', marginBottom: 8 }}>
        <p style={ST.successText}>Verification successful!</p>
        {verificationSuccess && secsLeft !== null && (
          <p style={ST.countdownText}>Continuing in {secsLeft}…</p>
        )}
      </div>
    );
  }
  
  function computeActionBarState() {
    const size = currentMatch.participants?.length || 0;
    const { codeHolderId, codeAggregatorId } = currentMatch;
    const iAmHolder = String(codeHolderId) === String(userId);
    const iAmAggregator = String(codeAggregatorId) === String(userId);
  
    let showEnter = false;
    let canSubmit = false;
    let onEnter = () => {};
  
    if (size === 2) {
      if (!iAmHolder && !currentMatch?.codeConfirmed) {
        showEnter = true;
        canSubmit = codeDigits.every((d) => d.length === 1);
        onEnter = () => {
          if (!currentMatch?._id) return;
          verifyCode(currentMatch._id, userId, codeDigits.join(''), sessionId);
        };
      }
    } else if (size === 3) {
      if (iAmAggregator && !currentMatch?.codeConfirmed) {
        showEnter = true;
  
        // Stage-gated: first submit only first code (server partial verify),
        // second submit sends both to backend (no optimistic green for pill #2)
        if (aggStage === 0) {
          const done1 = aggCodes.code1.join('').length === 3;
          canSubmit = done1;
          onEnter = async () => {
            const code1 = aggCodes.code1.join('');
            if (!code1 || code1.length < 3) return;
            const res = await verifyCodeStep(currentMatch._id, code1, 0);
            if (res.ok) {
              setAggVerified((v) => [code1, v[1]]);
              setAggStage(1);
              requestAnimationFrame(() => {
                aggregatorRefs.current[3]?.current?.focus();
              });
            } else {
              setShouldShake(true);
              setTimeout(() => setShouldShake(false), 600);
              setAggCodes((prev) => ({ ...prev, code1: ['', '', ''] }));
              requestAnimationFrame(() => {
                aggregatorRefs.current[0]?.current?.focus();
              });
            }
          };
        } else {
          const done2 = aggCodes.code2.join('').length === 3;
          canSubmit = done2;
          onEnter = () => {
            if (!currentMatch?._id) return;
            const code1 = aggVerified[0] || aggCodes.code1.join('');
            const code2 = aggCodes.code2.join('');
            // Final submit to server with both codes
            verifyCode(currentMatch._id, userId, `${code1},${code2}`, sessionId);
            // ❌ No optimistic green for pill #2; wait for codeConfirmed from server
          };
        }
      }
    }
  
    return { showEnter, canSubmit, onEnter };
  }
  
}

/* =================== STYLES (mirror Mixer CodeInputRow + Mixer Header) =================== */
const ST = {
  page: {
    minHeight: '100vh',
    background: '#fff',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    display: 'flex',
    flexDirection: 'column',
  },

  /* Mixer-style header */
  header: {
    container: {
      boxSizing: 'border-box',
      maxWidth: 680,
      width: '80%',
      margin: '12px auto 8px',
      padding: '0 12px',
      textAlign: 'center',
      color: '#0b0b0b',
    },
    title: {
      margin: 0,
      fontSize: '1.85rem',
      fontWeight: 800,
      lineHeight: 1.2,
      letterSpacing: '-0.01em',
    },
    highlight: {
      display: 'inline-block',
      backgroundColor: '#3579F6',
      color: '#fff',
      padding: '3px 10px',
      paddingBottom: 4,
      borderRadius: 6,
      transform: 'skewX(-6deg) rotate(-2deg)',
      marginLeft: 0,
    },
    highlightText: {
      display: 'inline-block',
      transform: 'skewX(6deg) rotate(2deg)',
    },
    subheading: {
      marginTop: 6,
      fontSize: '.9rem',
      color: '#6b7280',
      fontWeight: 500,
      lineHeight: 1.45,
    },
  },

  /* Code section */
  code: {
    container: {
      boxSizing: 'border-box',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 12px 96px',
      marginTop: 20,
    },
  },

  /* Divider under timer */
  dividerRow: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '.72rem',
    color: '#6b7280',
    fontWeight: 800,
    letterSpacing: '.08em',
    textTransform: 'uppercase',
    margin: '24px 0 -12px',
    width: '100%',
    maxWidth: 400,
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  dividerLine: { flex: 1, height: 1, backgroundColor: '#e5e7eb' },
  dividerText: { margin: '12px 0px' },

  /* Input rows */
  boxRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 10,
    margin: '12px 0 8px',
    width: '100%',
    maxWidth: 400,
  },
  inputBox: {
    width: 62,
    height: 70,
    borderRadius: 12,
    background: '#fff',
    border: '1px solid #e5e7eb',
    textAlign: 'center',
    fontSize: '1.5rem',
    fontWeight: 900,
    color: '#0f172a',
    textTransform: 'uppercase',
    fontVariantNumeric: 'tabular-nums',
    outline: 'none',
    transition: 'box-shadow .14s ease, border-color .14s ease',
    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
  },
  readOnlyBox: {
    width: 62,
    height: 70,
    borderRadius: 12,
    background: '#f8fafc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.5rem',
    fontWeight: 900,
    color: '#9ca3af',
    border: '1px solid #f8fafc',
    textTransform: 'uppercase',
    fontVariantNumeric: 'tabular-nums',
  },

  helper: {
    fontSize: '.85rem',
    lineHeight: 1.5,
    color: '#9ca3af',
    maxWidth: 300,
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: 10,
    marginBottom: 8,
    textAlign: 'center',
  },

  /* Action bar */
  actionBar: {
    position: 'fixed',
    inset: 'auto 0 0 0',
    background: '#fff',
    borderTop: '1px solid #eef0f2',
    display: 'flex',
    gap: 10,
    padding: '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))',
    boxSizing: 'border-box',
    maxWidth: 760,
    margin: '0 auto',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '100%',
    zIndex: 50,
  },
  actionButton: {
    flex: 1,
    height: 44,
    borderRadius: 12,
    border: '1px solid #0b0b0b',
    background: '#0b0b0b',
    color: '#fff',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  actionSecondary: {
    height: 44,
    borderRadius: 12,
    border: '1px solid #d1d5db',
    background: '#fff',
    color: '#0b0b0b',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  actionButtonDisabled: {
    borderColor: '#d1d5db',
    background: '#d1d5db',
    color: '#fff',
    cursor: 'not-allowed',
  },

  /* Fallback */
  fallbackContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: '#fff',
    padding: '1rem',
    textAlign: 'center',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  fallbackTitle: {
    fontSize: '1.2rem',
    fontWeight: 700,
    margin: '1rem 0 0.5rem',
    color: '#101828',
  },
  fallbackText: {
    fontSize: '0.95rem',
    color: '#475569',
    marginBottom: '1.5rem',
    maxWidth: 400,
  },
  fallbackBtn: {
    backgroundColor: '#3B82F6',
    color: '#fff',
    border: 'none',
    borderRadius: 9999,
    padding: '0.6rem 1.2rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
  },

  /* Header timer */
  timerWrapper: {
    marginTop: 18,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  timerLabelPill: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: 999,
    background: '#fff',
    border: '1px solid #e5e7eb',
    fontSize: '.7rem',
    color: '#374151',
    fontWeight: 800,
    letterSpacing: '.06em',
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  digitsRow: { display: 'flex', alignItems: 'baseline', gap: 4,     marginBottom: -6,
  },
  digit: {
    display: 'inline-block',
    fontSize: '2.5rem',
    lineHeight: 1,
    fontWeight: 900,
    letterSpacing: '-0.02em',
    color: '#0f172a',
    fontVariantNumeric: 'tabular-nums',
    marginTop: 8,
  },
  colon: {
    fontSize: '2rem',
    fontWeight: 900,
    color: '#9ca3af',
    lineHeight: 1,
    padding: '8px 4px',
    transform: 'translateY(-5px)',
  },

  /* Name pill */
  namePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '3px 8px 3px 2px',
    borderRadius: 999,
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#0f172a',
    fontSize: '.85rem',
    fontWeight: 800,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
    transform: 'translateY(4px)',
  },
  nameAvatar: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    objectFit: 'cover',
    display: 'block',
    border: '1px solid #fff',
  },

  /* Pager (locked for trio; still shown but disabled) */
  pagerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    margin: '6px 0 6px',
    marginTop: 10,
  },
  pagerBtn: {
    height: 32,
    width: 40,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    background: '#fff',
    color: '#0f172a',
    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
    cursor: 'pointer',
    transition: 'transform .12s ease, box-shadow .12s ease, opacity .12s ease',
    outline: 'none',
  },
  pagerBtnDisabled: { opacity: 0.45, cursor: 'not-allowed' },
  pagerIcon: { fontSize: 16 },
  pagerText: {
    minWidth: 48,
    textAlign: 'center',
    fontSize: 12,
    fontWeight: 500,
    letterSpacing: '.5em',
    color: '#0f172a',
  },

  /* Trio chips layout */
  trioTitle: {
    fontSize: '.85rem',
    lineHeight: 1.5,
    color: '#9ca3af',
    maxWidth: 300,
    marginLeft: 'auto',
    marginRight: 'auto',
    marginTop: 10,
    marginBottom: 24,
    textAlign: 'center'
  },
  trioRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    marginBottom: 12,
  },
  trioChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    borderRadius: 999,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#e5e7eb',
    background: '#fff',
    color: '#0f172a',
    fontSize: '.8rem',
    fontWeight: 800,
    lineHeight: 1,
    boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
  },
  trioChipActive: {
    borderColor: '#3579F6',
    boxShadow: '0 0 0 3px rgba(53,121,246,0.12)',
  },
  trioChipDone: {
    borderColor: '#10b981',
    background: '#ecfdf5',
  },
  trioAvatar: {
    width: 18,
    height: 18,
    borderRadius: '50%',
    objectFit: 'cover',
    display: 'block',
    border: '1px solid #fff',
  },
  trioName: { fontSize: '.78rem', fontWeight: 800 },
  trioIconDone: { fontSize: 16, color: '#10b981' },
  trioIconTodo: { fontSize: 14, color: '#9ca3af' },

  /* Success text */
  successText: {
    fontSize: '1rem',
    color: '#059669',
    fontWeight: 700,
    margin: 0,
  },
  countdownText: {
    fontSize: '0.9rem',
    color: '#6B7280',
    margin: '0.5rem 0 0',
  },
};
